import { Entity, Vec3 } from 'playcanvas';

import { CubicSpline } from './anim/spline';
import { AnimTrack, KeyEasing } from './anim-track';
import { Events } from './events';

type SceneCameraPose = {
    name: string;
    frame: number;
    position: Vec3;
    target: Vec3;
    easing?: KeyEasing;
};

/**
 * Per-camera animation track. Unlike the global CameraAnimTrack, this class
 * does NOT subscribe to timeline events automatically. Evaluation is driven
 * externally by the look-through system so that only the active camera fires
 * camera.setPose.
 *
 * Keyframe positions/targets are stored in **parent-local space** (matching
 * CameraMotionController). The evaluate() method transforms to world space
 * before firing camera.setPose.
 */
class SceneCameraAnimTrack implements AnimTrack {
    private poses: SceneCameraPose[] = [];
    private events: Events;
    private ownerEntity: Entity;
    private splineEval: ((frame: number, out: { position: Vec3; target: Vec3 }) => void) | null = null;

    constructor(events: Events, ownerEntity: Entity) {
        this.events = events;
        this.ownerEntity = ownerEntity;
    }

    get keys(): readonly number[] {
        return this.poses.map(p => p.frame);
    }

    addKey(frame: number): boolean {
        // Capture current entity position + forward as target (local space)
        const pos = this.ownerEntity.getLocalPosition().clone();
        const rot = this.ownerEntity.getLocalRotation();
        const fwd = new Vec3();
        rot.transformVector(Vec3.FORWARD, fwd);
        const target = new Vec3().add2(pos, fwd);

        const existingIndex = this.poses.findIndex(p => p.frame === frame);

        const newPose: SceneCameraPose = {
            name: `cam_${this.poses.length}`,
            frame,
            position: pos,
            target
        };

        if (existingIndex === -1) {
            this.poses.push(newPose);
            this.rebuildSpline();
            this.events.fire('track.keyAdded', frame);
        } else {
            this.poses[existingIndex] = newPose;
            this.rebuildSpline();
            this.events.fire('track.keyUpdated', frame);
        }
        return true;
    }

    removeKey(frame: number): boolean {
        const index = this.poses.findIndex(p => p.frame === frame);
        if (index === -1) return false;
        this.poses.splice(index, 1);
        this.rebuildSpline();
        this.events.fire('track.keyRemoved', frame);
        return true;
    }

    moveKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;
        const index = this.poses.findIndex(p => p.frame === fromFrame);
        if (index === -1) return false;

        const toIndex = this.poses.findIndex(p => p.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        const movedIndex = this.poses.findIndex(p => p.frame === fromFrame);
        this.poses[movedIndex].frame = toFrame;
        this.rebuildSpline();
        this.events.fire('track.keyMoved', fromFrame, toFrame);
        return true;
    }

    copyKey(fromFrame: number, toFrame: number): boolean {
        if (fromFrame === toFrame) return false;
        const source = this.poses.find(p => p.frame === fromFrame);
        if (!source) return false;

        const toIndex = this.poses.findIndex(p => p.frame === toFrame);
        if (toIndex !== -1) {
            this.poses.splice(toIndex, 1);
        }

        this.poses.push({
            name: `cam_${this.poses.length}`,
            frame: toFrame,
            position: source.position.clone(),
            target: source.target.clone()
        });

        this.rebuildSpline();
        this.events.fire('track.keyAdded', toFrame);
        return true;
    }

    getKeyEasing(frame: number): KeyEasing | undefined {
        const pose = this.poses.find(p => p.frame === frame);
        return pose?.easing;
    }

    setKeyEasing(frame: number, easing: KeyEasing | undefined): boolean {
        const pose = this.poses.find(p => p.frame === frame);
        if (!pose) return false;
        if (pose.easing === easing) return false;
        pose.easing = easing;
        this.rebuildSpline();
        this.events.fire('track.keyUpdated', frame);
        return true;
    }

    clear(): void {
        this.poses.length = 0;
        this.splineEval = null;
        this.events.fire('track.keysCleared');
    }

    snapshot(): SceneCameraPose[] {
        return this.poses.map(p => ({
            name: p.name,
            frame: p.frame,
            position: p.position.clone(),
            target: p.target.clone(),
            easing: p.easing
        }));
    }

    restore(snapshot: unknown): void {
        const poses = snapshot as SceneCameraPose[];
        this.poses = poses.map(p => ({
            name: p.name,
            frame: p.frame,
            position: p.position.clone(),
            target: p.target.clone(),
            easing: p.easing
        }));
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    /**
     * Evaluate the spline at the given frame. Transforms local-space result
     * to world space via the owner entity's parent transform.
     * Returns null if there are fewer than 2 keyframes.
     */
    evaluate(frame: number): { position: Vec3; target: Vec3 } | null {
        if (!this.splineEval) return null;

        const out = { position: new Vec3(), target: new Vec3() };
        this.splineEval(frame, out);

        // Transform from parent-local to world space
        const parent = this.ownerEntity.parent;
        if (parent) {
            const wtm = parent.getWorldTransform();
            wtm.transformPoint(out.position, out.position);
            wtm.transformPoint(out.target, out.target);
        }

        return out;
    }

    getPoses(): readonly SceneCameraPose[] {
        return this.poses;
    }

    loadPoses(posesData: SceneCameraPose[]): void {
        this.poses.length = 0;
        for (const p of posesData) {
            this.poses.push(p);
        }
        this.rebuildSpline();
        this.events.fire('track.keysLoaded');
    }

    rebuildSpline(): void {
        const duration = this.events.invoke('timeline.frames');
        const smoothness = this.events.invoke('timeline.smoothness');

        const orderedPoses = this.poses.slice()
        .filter(a => a.frame < duration)
        .sort((a, b) => a.frame - b.frame);

        const times = orderedPoses.map(p => p.frame);
        const points: number[] = [];
        for (const p of orderedPoses) {
            points.push(p.position.x, p.position.y, p.position.z);
            points.push(p.target.x, p.target.y, p.target.z);
        }

        if (orderedPoses.length > 1) {
            const easings = orderedPoses.map(p => p.easing);
            const spline = CubicSpline.fromPointsLooping(duration, times, points, smoothness, easings);
            const result: number[] = [];

            this.splineEval = (frame: number, out: { position: Vec3; target: Vec3 }) => {
                spline.evaluate(frame, result);
                out.position.set(result[0], result[1], result[2]);
                out.target.set(result[3], result[4], result[5]);
            };
        } else {
            this.splineEval = null;
        }
    }
}

export { SceneCameraAnimTrack };
export type { SceneCameraPose };
